import { ElectronAPI } from '../base/ElectronAPI';

import type { MeetingTranscriptionInputSelection } from '@electron/shared/meetings.types';

const Commands = {
  GetDictationEnabled: 'get-dictation-enabled',
  SetDictationEnabled: 'set-dictation-enabled',
  GetDictationInsertShortcut: 'get-dictation-insert-shortcut',
  GetDictationHoldShortcut: 'get-dictation-hold-shortcut',
  SetDictationHoldShortcut: 'set-dictation-hold-shortcut',
  GetDictationShortcut: 'get-dictation-shortcut',
  SetDictationShortcut: 'set-dictation-shortcut',
  GetDictationInputDevice: 'get-dictation-input-device',
  SetDictationInputDevice: 'set-dictation-input-device',
  GetDictationAICleanupEnabled: 'get-dictation-ai-cleanup-enabled',
  SetDictationAICleanupEnabled: 'set-dictation-ai-cleanup-enabled',
  EnsureMicrophoneAccess: 'ensure-dictation-microphone-access',
  StopDictationSession: 'stop-dictation-session',
  CancelDictationSession: 'cancel-dictation-session',
  SetDictationHeight: 'set-dictation-height',
} as const;

export class DictationAPI extends ElectronAPI {
  getEnabled(): Promise<boolean> {
    return this.invoke<boolean>(Commands.GetDictationEnabled);
  }

  setEnabled(enabled: boolean): Promise<void> {
    return this.invoke<void>(Commands.SetDictationEnabled, { enabled });
  }

  /** Empty while a held session is running, since that one ends on release. */
  getInsertShortcut(): Promise<string> {
    return this.invoke<string>(Commands.GetDictationInsertShortcut);
  }

  getHoldShortcut(): Promise<string> {
    return this.invoke<string>(Commands.GetDictationHoldShortcut);
  }

  /** Takes a single modifier like `fn`, or an empty string to turn push to talk off. */
  setHoldShortcut(shortcut: string): Promise<void> {
    return this.invoke<void>(Commands.SetDictationHoldShortcut, { shortcut });
  }

  getShortcut(): Promise<string> {
    return this.invoke<string>(Commands.GetDictationShortcut);
  }

  /** Takes a key combo like `command+shift+d`, and re-binds the hotkey immediately. */
  setShortcut(shortcut: string): Promise<void> {
    return this.invoke<void>(Commands.SetDictationShortcut, { shortcut });
  }

  getInputDevice(): Promise<MeetingTranscriptionInputSelection> {
    return this.invoke<MeetingTranscriptionInputSelection>(
      Commands.GetDictationInputDevice
    );
  }

  setInputDevice(selection: MeetingTranscriptionInputSelection): Promise<void> {
    return this.invoke<void>(Commands.SetDictationInputDevice, { selection });
  }

  getAICleanupEnabled(): Promise<boolean> {
    return this.invoke<boolean>(Commands.GetDictationAICleanupEnabled);
  }

  setAICleanupEnabled(enabled: boolean): Promise<void> {
    return this.invoke<void>(Commands.SetDictationAICleanupEnabled, {
      enabled,
    });
  }

  ensureMicrophoneAccess(): Promise<boolean> {
    return this.invoke<boolean>(Commands.EnsureMicrophoneAccess);
  }

  /** Ends the session and pastes the transcript. Same path the hotkey toggle takes. */
  stopSession(): Promise<void> {
    return this.invoke<void>(Commands.StopDictationSession);
  }

  /** Ends the session and discards the transcript. Same path Escape takes. */
  cancelSession(): Promise<void> {
    return this.invoke<void>(Commands.CancelDictationSession);
  }

  /** Grows the HUD upward as the transcript wraps. */
  setHeight(height: number): Promise<void> {
    return this.invoke<void>(Commands.SetDictationHeight, { height });
  }
}
